import { useQuery, useMutation } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import useApi from '@tabeeb/modules/shared/utils/API/hooks/useApi'

import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { trainingMaterialTitleFormatter } from '@tabeeb/modules/shared/utils/text'
import { TrainingMaterialBaseResponse } from '@tabeeb/contracts/microservices/swagger-article-service'
import { ArticlesQueryKeys } from '../queries'

export interface UseTrainingMaterialsProps {
  enabled?: boolean
  skip?: number
  take?: number
  search?: string | null
  contentId?: number | null
  attach?: boolean
  onError?: (error: unknown) => void
  onAttached?: () => void
}

export const useTrainingMaterials = ({
  enabled,
  skip,
  take,
  search,
  contentId,
  attach,
  onError,
  onAttached,
}: UseTrainingMaterialsProps) => {
  const dispatch = useDispatch()

  const { articleServiceApi: api } = useApi()

  const query = useQuery({
    queryKey: [ArticlesQueryKeys.TrainingMaterials, skip, take, search, contentId, attach],
    queryFn: () =>
      api.trainingMaterials.getAllAsync({
        skip: skip ?? 0,
        take: take ?? 50,
        search: search ?? '',
        contentId: contentId ?? undefined,
        attach: attach ?? false,
      }),
    enabled,
    onError,
  })

  const deleteMutation = useMutation({
    mutationFn: (trainingMaterial: TrainingMaterialBaseResponse) =>
      api.trainingMaterials.deleteAsync(trainingMaterial.Id),
    onSuccess: () => query.refetch(),
    onError: () => {
      dispatch(
        onAddErrorNotification({
          message: trainingMaterialTitleFormatter.format('Failed to delete training material'),
        })
      )
    },
  })

  const attachMutation = useMutation({
    mutationFn: api.contentTrainingMaterials.attachAsync,
    onSuccess: () => {
      query.refetch()
      onAttached?.()
    },
    onError: () => {
      dispatch(
        onAddErrorNotification({
          message: trainingMaterialTitleFormatter.format('Failed to attach training material'),
        })
      )
    },
  })

  return {
    loading: query.isLoading,
    loaded: query.isSuccess,
    totalCount: query.data?.TotalCount ?? 0,
    trainingMaterials: query.data?.Items ?? [],
    onDelete: deleteMutation.mutateAsync,
    isAttachInProgress: attachMutation.isLoading,
    onAttach: attachMutation.mutate,
    onReload: query.refetch,
  }
}

export default useTrainingMaterials
